"use client";

import { c as _c } from "react/compiler-runtime";
import { ReferralWorkflowTimeline, type ReferralWorkflowStatus, type ReferralWorkflowStep } from "@/configuration/groups/usrc/components/referral-timeline";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { ensurePatientOutputSchema } from "@/src/models/ensure-patient-output";
import { TaskStatus, type Task } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

// Step outputs are read field by field rather than through the producers' zod schemas: those live
// beside the EHR integrations, which pull server-only modules into the client bundle.
function outputString(step: TaskStep | undefined, key: string): string | null {
  const value = step?.output?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}
function outputNumber(step: TaskStep | undefined, key: string): number | null {
  const value = step?.output?.[key];
  return typeof value === "number" ? value : null;
}
function stepStatus(step: TaskStep | undefined, confirmed: boolean, options: {
  needsAction?: boolean;
  skipped?: boolean;
} = {}): ReferralWorkflowStatus {
  if (!step) return "not_run";
  if (step.status === TaskStepStatus.FAILED) return "failed";
  if (options.needsAction) return "needs_action";
  if (step.status === TaskStepStatus.ASYNC || step.status === TaskStepStatus.PAUSED) return "waiting";
  // Early exits are persisted as completed too, so require success evidence before the checkmark.
  if (step.status === TaskStepStatus.COMPLETED && confirmed) return options.skipped ? "not_needed" : "automated";
  return "needs_action";
}
export function UsrcReferralSteps(t0) {
  const $ = _c(35);
  const {
    task,
    taskSteps
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== taskSteps) {
    const orderedTaskSteps = taskSteps.toSorted(_temp);
    const findStep = key => orderedTaskSteps.find(step => step.stepKey === key);
    const lookupStep = findStep(StepKey.USRC_REGISTRATION_LOOKUP);
    const patientStep = findStep(StepKey.ENSURE_PATIENT);
    const questionnaireStep = findStep(StepKey.USRC_REGISTRATION_QUESTIONNAIRE);
    const uploadStep = findStep(StepKey.EHR_DOC_UPLOAD);
    const supportingStep = findStep(StepKey.SUPPORTING_SECTIONS_UPLOAD);
    const noteStep = findStep(StepKey.USRC_REGISTRATION_NOTE);
    const conversationStep = findStep(StepKey.USRC_REGISTRATION_CONVERSATION);
    const disposition = outputString(lookupStep, "disposition");
    const matchedRegistrationId = outputNumber(lookupStep, "registrationId");
    const registration = ensurePatientOutputSchema.safeParse(patientStep?.output);
    const unmappedClassification = outputString(uploadStep, "outcome") === "unmapped_document_type" ? outputString(uploadStep, "classification") : null;
    const unmapped = outputString(uploadStep, "outcome") === "unmapped_document_type";
    const discharged = disposition === "involuntarily_discharged";
    const questionnaireOutcome = outputString(questionnaireStep, "outcome");
    const noQuestionnaire = questionnaireOutcome === "not_applicable";
    const questionnaireUnpostable = questionnaireOutcome === "unpostable";
    const uploaded = outputString(uploadStep, "fileId") !== null;
    const supportingUploads = Array.isArray(supportingStep?.output?.uploads) ? supportingStep.output.uploads : null;
    const supportingCount = supportingUploads ? supportingUploads.length : null;
    const supportingDone = supportingStep?.status === TaskStepStatus.COMPLETED;
    t3 = {
      title: "Find the patient's registration",
      status: stepStatus(lookupStep, disposition !== null, {
        needsAction: discharged
      }),
      summary: discharged ? "This patient was involuntarily discharged. The referral stops here." : "Searched Intake Manager for an existing registration for this patient.",
      content: disposition ? <p className="text-sm">{disposition === "update" ? `Matched existing registration ${matchedRegistrationId ?? "(id not recorded)"}.` : disposition === "create" ? "No existing registration; a new one was created in the next step." : "Intake Manager reports this patient as involuntarily discharged. Do not register them again."}</p> : undefined
    };
    t4 = {
      title: "Create or update the registration",
      status: stepStatus(patientStep, registration.success),
      summary: "Registered the patient in Intake Manager, or updated the registration the lookup found.",
      content: registration.success ? <p className="text-sm">{registration.data.created ? "New registration" : "Existing registration"}{" "}<strong>{registration.data.patientEhrId}</strong>. Use this ID to find the chart in Intake Manager.</p> : undefined
    };
    let t9;
    if ($[9] !== questionnaireUnpostable) {
      t9 = questionnaireUnpostable ? <p className="text-sm">USRC has not published the questionnaire question and answer IDs, so the extracted answers cannot be posted yet. Enter them in Intake Manager by hand.</p> : undefined;
      $[9] = questionnaireUnpostable;
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    t5 = {
      title: "Answer the dialysis questionnaire",
      status: stepStatus(questionnaireStep, true, {
        needsAction: questionnaireUnpostable,
        skipped: noQuestionnaire
      }),
      summary: noQuestionnaire ? "This packet did not include a dialysis questionnaire." : questionnaireUnpostable ? "No answers were posted. Enter them in Intake Manager by hand." : "Posted the isolation, equipment and mobility answers to the registration.",
      content: t9
    };
    t6 = {
      title: "Upload the referral packet",
      status: stepStatus(uploadStep, uploaded, {
        needsAction: unmapped
      }),
      summary: unmapped ? "The referral packet was not uploaded." : "Indexed the referral PDF to the registration in Intake Manager.",
      content: unmapped ? <p className="text-sm">No Intake Manager document type is configured for{" "}<strong>{unmappedClassification ?? "this classification"}</strong>, so the document was not uploaded. File it by hand and ask for the document type ID to be added.</p> : undefined
    };
    t7 = {
      title: "Upload supporting documents",
      status: stepStatus(supportingStep, supportingDone, {
        skipped: supportingCount === 0
      }),
      summary: supportingCount === 0 ? "This packet had no additional supporting clinical records." : supportingCount !== null ? `Indexed ${supportingCount} supporting document${supportingCount === 1 ? "" : "s"} to the registration.` : "Index clinical records, labs, imaging, and questionnaires to the registration in Intake Manager.",
      content: supportingCount !== null && supportingCount > 0 ? <p className="text-sm">Indexed {supportingCount} supporting document{supportingCount === 1 ? "" : "s"} (labs, notes, questionnaires) directly to the registration in Intake Manager.</p> : undefined
    };
    let t10;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <p className="text-sm">Intake Manager returns no confirmation for this write. Check the note exists.</p>;
      $[11] = t10;
    } else {
      t10 = $[11];
    }
    t8 = {
      title: "Add the registration note",
      status: stepStatus(noteStep, true),
      summary: "Posted a staff Note with pending medical-records and clearance flags (not the Referral Notes field).",
      content: t10
    };
    t1 = "Send the handoff conversation";
    t2 = stepStatus(conversationStep, true);
    $[0] = taskSteps;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="text-sm">Intake Manager returns no confirmation for this write. Check the message exists.</p>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t1 || $[14] !== t2) {
    t10 = {
      title: t1,
      status: t2,
      summary: "Posted the admissions handoff message on the registration's conversation topic.",
      content: t9
    };
    $[13] = t1;
    $[14] = t2;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let isCompleted;
  let isProcessing;
  let steps;
  if ($[16] !== t10 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== task.status) {
    steps = [t3, t4, t5, t6, t7, t8, t10];
    isCompleted = task.status === TaskStatus.COMPLETED;
    isProcessing = task.status === TaskStatus.PROCESSING;
    const hasFailedStep = steps.some(_temp2);
    const needsAttention = steps.some(_temp3);
    const hasUnfinishedStep = steps.some(_temp4);
    const t11 = hasFailedStep ? "failed" : needsAttention ? "needs_action" : hasUnfinishedStep && !isCompleted ? "waiting" : "verify";
    const t12 = isCompleted ? "Automation finished and completed this task on its own. Nobody has checked Intake Manager yet." : hasFailedStep || needsAttention ? "Resolve the outstanding steps above before verifying this task." : hasUnfinishedStep ? "The workflow is still running. Verify the results when it finishes." : "Confirm the registration, questionnaire, referral document, staff note and conversation in Intake Manager.";
    let t13;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <p className="text-sm">Open the registration in Intake Manager and confirm the referral landed. The staff note and conversation return no confirmation, so check those by hand.</p>;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    let t14;
    if ($[28] !== t11 || $[29] !== t12) {
      t14 = {
        title: "Verify in Intake Manager",
        status: t11,
        summary: t12,
        content: t13
      };
      $[28] = t11;
      $[29] = t12;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    steps.push(t14);
    $[16] = t10;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = task.status;
    $[24] = isCompleted;
    $[25] = isProcessing;
    $[26] = steps;
  } else {
    isCompleted = $[24];
    isProcessing = $[25];
    steps = $[26];
  }
  const t11 = !isCompleted && taskSteps.length === 0 ? "No Intake Manager sync steps have been recorded for this task yet." : undefined;
  let t12;
  if ($[31] !== isProcessing || $[32] !== steps || $[33] !== t11) {
    t12 = <ReferralWorkflowTimeline steps={steps} isProcessing={isProcessing} waitingMessage={t11} />;
    $[31] = isProcessing;
    $[32] = steps;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}
function _temp4(step_2) {
  return step_2.status === "not_run" || step_2.status === "waiting";
}
function _temp3(step_1) {
  return step_1.status === "needs_action" || step_1.status === "failed";
}
function _temp2(step_0) {
  return step_0.status === "failed";
}
function _temp(a, b) {
  return a.stepIndex - b.stepIndex;
}